import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiBook, 
  FiHelpCircle, 
  FiVideo, 
  FiMessageSquare, 
  FiMail, 
  FiSearch,
  FiChevronRight,
  FiStar,
  FiDownload,
  FiUser,
  FiSettings,
  FiShield,
  FiGlobe,
  FiFileText,
  FiUsers,
  FiBriefcase,
  FiAward
} = FiIcons;

const helpCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the basics and get up and running quickly',
    icon: FiBook,
    color: 'bg-blue-500',
    articles: [
      { id: 1, title: 'Introduction to ReferBridge', views: 245 },
      { id: 2, title: 'Setting up your profile', views: 189 },
      { id: 3, title: 'Navigating the dashboard', views: 156 }
    ]
  },
  {
    id: 'faq',
    title: 'FAQs',
    description: 'Find answers to common questions',
    icon: FiHelpCircle,
    color: 'bg-green-500',
    articles: [
      { id: 4, title: 'How do I reset my password?', views: 321 },
      { id: 5, title: 'What browsers are supported?', views: 142 },
      { id: 6, title: 'How do I export reports?', views: 98 }
    ]
  },
  {
    id: 'tutorials',
    title: 'Video Tutorials',
    description: 'Step-by-step guides to master ReferBridge',
    icon: FiVideo,
    color: 'bg-purple-500',
    articles: [
      { id: 7, title: 'Managing referrals', views: 278 },
      { id: 8, title: 'Setting up dealrooms', views: 165 },
      { id: 9, title: 'Using the analytics dashboard', views: 203 }
    ]
  },
  {
    id: 'community',
    title: 'Community Support',
    description: 'Connect with other users and experts',
    icon: FiUsers,
    color: 'bg-yellow-500',
    articles: [
      { id: 10, title: 'Best practices for patient engagement', views: 156 },
      { id: 11, title: 'Top tips for providers', views: 134 },
      { id: 12, title: 'Case study: Successful implementation', views: 98 }
    ]
  }
];

const popularTopics = [
  { title: 'Account Setup', icon: FiUser, path: '/help/account-setup' },
  { title: 'Billing & Payments', icon: FiSettings, path: '/help/billing' },
  { title: 'Security & Privacy', icon: FiShield, path: '/help/security' },
  { title: 'Integrations', icon: FiGlobe, path: '/help/integrations' },
  { title: 'Reports & Analytics', icon: FiFileText, path: '/help/analytics' }
];

const HelpHub = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredCategories = helpCategories.filter(category => 
    category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.articles.some(article => 
      article.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Help Center
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-6 max-w-lg mx-auto text-xl text-blue-100 sm:max-w-3xl"
            >
              Find answers, watch tutorials, and get the most out of ReferBridge
            </motion.p>
            
            {/* Search Bar */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-10 max-w-2xl mx-auto"
            >
              <div className="relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-4 border-0 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                  placeholder="Search help articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Popular Topics */}
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-gray-900">Popular Topics</h2>
          <p className="mt-2 text-gray-600">Quick access to frequently visited help topics</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {popularTopics.map((topic, index) => (
            <motion.div
              key={topic.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-lg shadow p-6 text-center cursor-pointer border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="mx-auto bg-blue-100 rounded-full p-3 w-12 h-12 flex items-center justify-center">
                <SafeIcon icon={topic.icon} className="text-blue-600 h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">{topic.title}</h3>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Help Categories */}
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">Help Categories</h2>
          <p className="mt-2 text-gray-600">Browse our comprehensive help resources</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer border border-gray-100 hover:border-blue-300 transition-all"
            >
              <div className={`${category.color} p-6 text-white`}>
                <div className="flex items-center">
                  <SafeIcon icon={category.icon} className="h-8 w-8" />
                  <h3 className="ml-4 text-xl font-bold">{category.title}</h3>
                </div>
                <p className="mt-2 opacity-90">{category.description}</p>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {category.articles.map((article) => (
                    <li key={article.id} className="flex items-start justify-between">
                      <div className="flex items-start">
                        <SafeIcon icon={FiChevronRight} className="text-gray-400 mt-1 flex-shrink-0" />
                        <span className="text-gray-700 hover:text-blue-600">{article.title}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <SafeIcon icon={FiStar} className="mr-1" />
                        {article.views}
                      </div>
                    </li>
                  ))}
                </ul>
                <button className="mt-6 w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  View All {category.title}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900">Need More Help?</h2>
            <p className="mt-4 text-lg text-gray-600">
              Our support team is here to assist you with any questions or issues.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="pt-6"
            >
              <div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
                <div className="-mt-6">
                  <div>
                    <span className="inline-flex items-center justify-center p-3 bg-blue-500 rounded-md shadow-lg">
                      <SafeIcon icon={FiMessageSquare} className="h-6 w-6 text-white" />
                    </span>
                  </div>
                  <h3 className="mt-8 text-lg font-medium text-gray-900">Live Chat</h3>
                  <p className="mt-5 text-base text-gray-600">
                    Chat with our support team in real-time for immediate assistance.
                  </p>
                  <div className="mt-6">
                    <button className="text-blue-600 font-medium hover:text-blue-800">
                      Start Chat
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="pt-6"
            >
              <div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
                <div className="-mt-6">
                  <div>
                    <span className="inline-flex items-center justify-center p-3 bg-green-500 rounded-md shadow-lg">
                      <SafeIcon icon={FiMail} className="h-6 w-6 text-white" />
                    </span>
                  </div>
                  <h3 className="mt-8 text-lg font-medium text-gray-900">Email Support</h3>
                  <p className="mt-5 text-base text-gray-600">
                    Send us an email and we'll get back to you within 24 hours.
                  </p>
                  <div className="mt-6">
                    <a href="mailto:support@referbridge.com" className="text-blue-600 font-medium hover:text-blue-800">
                      support@referbridge.com
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -5 }}
              className="pt-6"
            >
              <div className="flow-root bg-gray-50 rounded-lg px-6 pb-8">
                <div className="-mt-6">
                  <div>
                    <span className="inline-flex items-center justify-center p-3 bg-purple-500 rounded-md shadow-lg">
                      <SafeIcon icon={FiDownload} className="h-6 w-6 text-white" />
                    </span>
                  </div>
                  <h3 className="mt-8 text-lg font-medium text-gray-900">Documentation</h3>
                  <p className="mt-5 text-base text-gray-600">
                    Download our comprehensive user guides and documentation.
                  </p>
                  <div className="mt-6">
                    <button className="text-blue-600 font-medium hover:text-blue-800">
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpHub;